// Edit-load recovery of what the user did to the relation's Leistungen. The wizard
// regenerates those positions on every save (delete + insert), so whatever is only on
// the saved rows — a Bezeichnung or Einzelpreis changed by hand (here or on the
// Bierdeckel), the Beziehung picked for the price rule — has to be read back first,
// or the next save silently undoes it. A hand edit is told from a changed catalog
// price/name by the position's lock flag, which both the wizard and the Bierdeckel
// set. Pure, so it is testable without the booking graph. (#7044)

import { BookingV2PricingRelationIds, BookingV2RelationLineOverride } from '../dtos/booking-v2.dtos';

// The parts of a saved relation position the recovery reads (InvoicePosition fits).
export interface RecoverableRelationPosition {
    title?: string | null;
    price?: number | null;
    priceLock?: boolean | null;
    titleLock?: boolean | null;
    product?: { id?: number | null; name?: string | null; price?: number | null } | null;
    dynamicPrice?: {
        ruleName?: string | null;
        rulePrice?: number | null;
        tenantRelation?: { id?: number | null } | null;
    } | null;
}

export interface RecoveredRelationLines {
    overrides: Record<number, BookingV2RelationLineOverride>;
    pricingRelationIds: BookingV2PricingRelationIds;
}

// Decimal columns can arrive as strings ("45.00").
function toCents(value: number | string | null | undefined): number | null {
    const n = value == null || value === '' ? NaN : Number(value);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export function recoverRelationLines(
    positions: readonly RecoverableRelationPosition[],
    bookingRelationId: number | null,
): RecoveredRelationLines {
    const overrides: Record<number, BookingV2RelationLineOverride> = {};
    const pricingRelationIds: Record<number, number> = {};
    const seen = new Set<number>();

    for (const position of positions) {
        const productId = position.product?.id;
        if (productId == null || seen.has(productId)) {
            continue;
        }
        seen.add(productId);

        const dynamicPrice = position.dynamicPrice;
        const override: BookingV2RelationLineOverride = {};

        // The product name counts as "not edited" too: positions saved before #7044
        // carry it even though the rule had a name.
        const title = position.title?.trim();
        const autoTitles = [dynamicPrice?.ruleName, position.product?.name]
            .map(t => t?.trim())
            .filter(Boolean);
        if (position.titleLock && title && !autoTitles.includes(title)) {
            override.title = title;
        }

        const priceCents = toCents(position.price);
        const referenceCents = toCents(dynamicPrice?.rulePrice ?? position.product?.price);
        if (position.priceLock && priceCents != null && referenceCents != null && priceCents !== referenceCents) {
            override.price = priceCents / 100;
        }

        if (override.title !== undefined || override.price !== undefined) {
            overrides[productId] = override;
        }

        const ruleRelationId = dynamicPrice?.tenantRelation?.id;
        if (ruleRelationId != null && ruleRelationId !== bookingRelationId) {
            pricingRelationIds[productId] = ruleRelationId;
        }
    }

    return { overrides, pricingRelationIds };
}
